import { useState } from 'react'
import { useWindowStore } from '../../stores/windowStore'
import './FileManager.css'

interface FileItem {
  name: string
  type: 'folder' | 'file'
  size?: string
  modified?: string
  content?: string
}

interface Directory {
  [path: string]: FileItem[]
}

// Map exe files to app IDs
const exeToAppId: Record<string, { appId: string; title: string; icon: string }> = {
  'calculator.exe': { appId: 'calculator', title: 'Calculator', icon: 'calculator' },
  'notepad.exe': { appId: 'notepad', title: 'Notepad', icon: 'file-text' },
  'terminal.exe': { appId: 'terminal', title: 'Terminal', icon: 'terminal' },
  'minesweeper.exe': { appId: 'minesweeper', title: 'Minesweeper', icon: 'bomb' },
}

const fileSystem: Directory = {
  '/': [
    { name: 'DOCUMENTS', type: 'folder' },
    { name: 'PROGRAMS', type: 'folder' },
    { name: 'GAMES', type: 'folder' },
    { name: 'SYSTEM', type: 'folder' },
    { name: 'README.TXT', type: 'file', size: '1.2K', modified: '2025-01-01' },
  ],
  '/DOCUMENTS': [
    { name: '..', type: 'folder' },
    { name: 'about.txt', type: 'file', size: '2.4K', modified: '2025-01-01', content: 'Persona Science - Persistent Identity Intelligence' },
    { name: 'solutions.txt', type: 'file', size: '1.8K', modified: '2025-01-01', content: 'Multi-Domain Fusion | Edge Devices | Persona Analytics' },
    { name: 'contact.txt', type: 'file', size: '0.5K', modified: '2025-01-01', content: 'Email: contact@personascience.com' },
    { name: 'mission.txt', type: 'file', size: '3.1K', modified: '2025-01-01', content: 'To fuse multi-signal and side-channel data into unified identity intelligence.' },
  ],
  '/PROGRAMS': [
    { name: '..', type: 'folder' },
    { name: 'calculator.exe', type: 'file', size: '45K', modified: '2025-01-01' },
    { name: 'notepad.exe', type: 'file', size: '32K', modified: '2025-01-01' },
    { name: 'terminal.exe', type: 'file', size: '128K', modified: '2025-01-01' },
  ],
  '/GAMES': [
    { name: '..', type: 'folder' },
    { name: 'minesweeper.exe', type: 'file', size: '64K', modified: '2025-01-01' },
    { name: 'highscores.dat', type: 'file', size: '0.2K', modified: '2025-01-01' },
  ],
  '/SYSTEM': [
    { name: '..', type: 'folder' },
    { name: 'config.sys', type: 'file', size: '0.8K', modified: '2025-01-01' },
    { name: 'autoexec.bat', type: 'file', size: '0.3K', modified: '2025-01-01' },
    { name: 'persona.ini', type: 'file', size: '1.2K', modified: '2025-01-01' },
  ],
}

export default function FileManager() {
  const [currentPath, setCurrentPath] = useState('/')
  const [selectedItem, setSelectedItem] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const openWindow = useWindowStore((state) => state.openWindow)

  const files = fileSystem[currentPath] || []

  const handleItemClick = (item: FileItem) => {
    setSelectedItem(item.name)
    if (item.type === 'file' && item.content) {
      setPreview(item.content)
    } else {
      setPreview(null)
    }
  }

  const handleItemDoubleClick = (item: FileItem) => {
    if (item.type === 'folder') {
      if (item.name === '..') {
        const parts = currentPath.split('/').filter(Boolean)
        parts.pop()
        setCurrentPath('/' + parts.join('/') || '/')
      } else {
        setCurrentPath(currentPath === '/' ? `/${item.name}` : `${currentPath}/${item.name}`)
      }
      setSelectedItem(null)
      setPreview(null)
    } else if (item.type === 'file' && item.name.endsWith('.exe')) {
      // Launch the program
      const appInfo = exeToAppId[item.name]
      if (appInfo) {
        openWindow(appInfo.appId, appInfo.title, appInfo.icon)
      }
    }
  }

  return (
    <div className="filemanager-app">
      <div className="filemanager-toolbar">
        <span className="filemanager-path">C:{currentPath.toUpperCase()}</span>
      </div>

      <div className="filemanager-content">
        <div className="filemanager-list">
          <div className="filemanager-header">
            <span className="col-name">Name</span>
            <span className="col-size">Size</span>
            <span className="col-date">Modified</span>
          </div>
          {files.map((item) => (
            <div
              key={item.name}
              className={`filemanager-item ${selectedItem === item.name ? 'selected' : ''}`}
              onClick={() => handleItemClick(item)}
              onDoubleClick={() => handleItemDoubleClick(item)}
            >
              <span className="col-name">
                <span className="item-icon">
                  {item.type === 'folder' ? '📁' : item.name.endsWith('.exe') ? '⚙️' : '📄'}
                </span>
                {item.name}
              </span>
              <span className="col-size">{item.size || '<DIR>'}</span>
              <span className="col-date">{item.modified || ''}</span>
            </div>
          ))}
        </div>

        {preview && (
          <div className="filemanager-preview">
            <div className="preview-header">Preview</div>
            <pre className="preview-content">{preview}</pre>
          </div>
        )}
      </div>

      <div className="filemanager-statusbar">
        <span>{files.length} item(s)</span>
        {selectedItem && <span>Selected: {selectedItem}</span>}
      </div>
    </div>
  )
}
